<script setup lang="ts">
import { reactive, toRef } from "vue";

import { EditableList } from "@/components/EditableList";

import { Input, Question, DatePicker } from "@/components/fields";
import { isNumber } from "@/utils";
import { HypertensionReading } from "@/entities/conditions";
import readingValidator from "./validators";
import { formatDate } from "@/utils/date";

export interface HypertensionFormState {
    currentReading: HypertensionReading | null,
    showCloseModal: boolean,
    isEdit: boolean,
}

export interface Props {
    modelValue: HypertensionReading[],
    diagnosisDate: string | null
}

const props = defineProps<Props>();

const state = reactive<HypertensionFormState>({
    currentReading: null,
    showCloseModal: false,
    isEdit: false,
});

const emit = defineEmits<{
    (on:"update:modelValue", value: HypertensionReading[]): void
}>();

const validation = readingValidator(toRef(state, "currentReading"));

const addReading = () => {
    state.currentReading = {
        syntheticId: Date.now(),
        diastolic: null,
        systolic: null,
        readingdate: null,
    };
};

const handleUpdate = (items: HypertensionReading[]) => {
    emit("update:modelValue", items);
};

const handleUpdateItem = (item: HypertensionReading | null) => {
    state.currentReading = item;
};

const handleSave = (items: HypertensionReading[]) => {
    state.currentReading = null;
    emit("update:modelValue", items);
};

const currentDate = new Date();
const maxDate = currentDate;
</script>
<template>
    <EditableList
        :current-item="state.currentReading"
        :items="modelValue"
        header="High Blood Pressure Reading"
        @update:items="handleUpdate"
        @update:item="handleUpdateItem"
        @save="handleSave"
    >
        <template
            v-for="item in modelValue"
            #[item.syntheticId]
            :key="item.syntheticId"
        >
            <span
                class="col-md-2 "
            >
                {{ item.systolic }} / {{ item.diastolic }}
            </span>
            <span class="col-md-2 ">
                {{ formatDate(item.readingdate) }}
            </span>
        </template>
        <template
            v-if="state.currentReading"
            #default
        >
            <Question
                name="systolic"
                label="Systolic"
            >
                <Input
                    v-model.number="state.currentReading.systolic"
                    type="number"
                    inputmode="numeric"
                    placeholder="Systolic"
                    :filter="isNumber"
                    :validation="validation.systolic"
                />
            </Question>
            <Question
                name="diastolic"
                label="Diastolic"
            >
                <Input
                    v-model.number="state.currentReading.diastolic"
                    type="number"
                    inputmode="numeric"
                    placeholder="Diastolic"
                    :filter="isNumber"
                    :validation="validation.diastolic"
                />
            </Question>
            <Question
                name="readingdate"
                label="Reading date"
            >
                <DatePicker
                    id="readingdate"
                    :key="state.currentReading.syntheticId"
                    v-model="state.currentReading.readingdate"
                    :max-date="maxDate"
                    :min-date="new Date(props.diagnosisDate || 0)"
                    :show-day-field="false"
                    :validation="validation.readingdate"
                />
            </Question>
        </template>
    </EditableList>
    <button
        v-if="state.currentReading === null"
        id="add-pressure-reading"
        type="button"
        class="btn btn-primary btn-block font-weight-bold align-self-center mb-2"
        @click.prevent="addReading"
    >
        {{ modelValue.length ? "Add another Reading" : "Add a reading" }}
    </button>
</template>
